import { strings } from "@perceptron/constants";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@perceptron/ui/components/dialog";
import { Icon } from "@perceptron/ui/components/icon";

import { DEFAULT_SHORTCUTS, type PlayerShortcut } from "./hooks/usePlayerShortcuts";

type GuideRow = { label: string; keys: string };

/**
 * One row per guide label. A row with one shortcut lists its alternatives
 * ("Space / K"); a row with several pairs them up column by column ("← / → · J / L").
 */
export function shortcutGuideRows(shortcuts: readonly PlayerShortcut[]): GuideRow[] {
  const copy = strings.videoShortcuts;
  const groups: { label: string; captions: string[][] }[] = [];
  for (const { guide } of shortcuts) {
    if (!guide) continue;
    const captions = guide.keys.map((key) => (key === "Space" ? copy.space : key));
    const last = groups.at(-1);
    if (last?.label === copy[guide.label]) last.captions.push(captions);
    else groups.push({ label: copy[guide.label], captions: [captions] });
  }
  return groups.map(({ label, captions }) => {
    if (captions.length === 1) return { label, keys: captions[0]!.join(" / ") };
    const columns = Math.max(...captions.map((keys) => keys.length));
    const keys = Array.from({ length: columns }, (_, column) =>
      captions.flatMap((keys) => keys[column] ?? []).join(" / "),
    ).join(" · ");
    return { label, keys };
  });
}

export function KeyboardShortcutsGuide({
  open,
  onOpenChange,
  container,
  returnFocus,
  shortcuts: playerShortcuts = DEFAULT_SHORTCUTS,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  container?: HTMLElement | null;
  returnFocus?: HTMLElement | null;
  shortcuts?: readonly PlayerShortcut[];
}) {
  const copy = strings.videoShortcuts;
  const shortcuts = [
    ...shortcutGuideRows(playerShortcuts),
    // Escape belongs to this dialog and the player root, not the shortcut table.
    { label: copy.dismiss, keys: "Escape" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="dark max-h-[calc(100dvh-2rem)] max-w-md overflow-y-auto rounded-lg border-border bg-background-alt-2 text-foreground shadow-none"
        hideCloseButton
        portalContainer={container}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocus?.focus();
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <DialogHeader className="pr-6 text-left">
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <dl className="flex flex-col gap-3 text-sm">
          {shortcuts.map(({ label, keys }) => (
            <div className="flex items-center justify-between gap-4" key={label}>
              <dt>{label}</dt>
              <dd className="shrink-0 rounded border border-border bg-background-alt px-2 py-1 text-xs">
                <kbd className="font-sans">{keys}</kbd>
              </dd>
            </div>
          ))}
        </dl>
        <DialogClose
          aria-label={copy.close}
          className="absolute right-4 top-4 rounded-sm text-foreground-secondary hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          type="button"
        >
          <Icon aria-hidden className="size-4" name="x" />
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
